import React, { useCallback, useMemo } from 'react';
import { formatTranslation } from 'intl';
import {
  InspectionTemplateTranslationKeys,
  InspectionTranslationKeys,
} from 'intl/keys';
import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { RichTextView } from 'components/common/RichTextEditor/RichTextView';
import { MemoChecklistItemPreview } from '../ChecklistItemPreview';
import { GraphicUploader } from 'components/common/graphicUploader';
import { useSectionTemplateDocumentRouteSync } from 'components/common/graphicUploader/documentRouteSync';
import { useGraphicUploader } from 'components/common/graphicUploader/useLocalGraphicUploader';
import { useDisplayedFilesStoreHasFiles } from 'components/common/gallery/filesStore';
import { fieldLabelSx } from 'shared/styles/typography';
import { templateAttachmentsWrapperSx } from '../ChecklistItemPreview/styles';
import { buildChecklistTreeFromSources } from 'shared/domain/checklistTree';
import { MemoTreeRenderer } from 'components/common/checklistTree';
import { useInspectionTemplate } from 'store/inspection';
import {
  createTemplateChecklistSectionDocumentsBaseUrl,
} from 'shared/domain/template/templateDocumentPaths';
import {
  DocumentToSectionBinder,
  IDocumentBinder,
} from 'shared/domain/document/documentBinder';
import { getDocumentScopeKey } from 'shared/domain/document/documentScope';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { createInspectionTemplateViewSectionRoute } from 'views/inspections/single/helpers';
import { ChecklistTreePreviewProps } from './types';

function SectionDocumentsPreview({
  templateId,
  sectionId,
}: {
  templateId: string;
  sectionId: string;
}): React.ReactElement | null {
  const intl = useIntl();
  const projectId = useAppUrlParams(UrlParamName.PROJECT_ID);
  const getSectionBaseUrl = useCallback(
    () => createInspectionTemplateViewSectionRoute(projectId, templateId, sectionId),
    [projectId, sectionId, templateId]
  );
  const sectionRouteSync = useSectionTemplateDocumentRouteSync(
    undefined,
    getSectionBaseUrl,
    templateId,
    sectionId
  );
  const { inspectionTemplateDocuments } = useInspectionTemplate();

  const config = React.useMemo(
    () => ({
      baseUrl: createTemplateChecklistSectionDocumentsBaseUrl(
        templateId,
        sectionId
      ),
      documentScopeKey: getDocumentScopeKey({ templateId }),
      previewOnly: true as const,
      internalEntityBaseUrl: sectionRouteSync.internalEntityBaseUrl,
      autoOpenCheck: sectionRouteSync.autoOpenCheck,
      documentBinderFactory: (): IDocumentBinder =>
        new DocumentToSectionBinder({
          templateId,
          sectionId,
        }),
    }),
    [sectionId, sectionRouteSync.autoOpenCheck, sectionRouteSync.internalEntityBaseUrl, templateId]
  );

  const uploader = useGraphicUploader(
    inspectionTemplateDocuments.sectionDocuments[sectionId],
    config
  );
  const hasTemplateFiles = useDisplayedFilesStoreHasFiles(uploader.filesStore);

  if (!hasTemplateFiles) {
    return null;
  }

  return (
    <Box sx={[templateAttachmentsWrapperSx, { mt: 1.5 }]}>
      <Typography component='p' sx={fieldLabelSx}>
        {formatTranslation(
          intl,
          InspectionTranslationKeys.sectionPreviewFieldLabelTemplateAttachments
        )}
      </Typography>
      <GraphicUploader {...uploader} />
    </Box>
  );
}

function ChecklistTreePreview({
  templateId,
  checklist,
  sections,
  expandedIds,
  onExpandedChange,
}: ChecklistTreePreviewProps): React.ReactElement {
  const intl = useIntl();

  const tree = useMemo(() => {
    return buildChecklistTreeFromSources({
      sections,
      items: checklist,
      excludeDeleted: true,
    });
  }, [sections, checklist]);

  return (
    <MemoTreeRenderer
      tree={tree}
      expandedIds={expandedIds}
      onExpandedChange={onExpandedChange}
      resolveSectionTitle={(node) =>
        node.data.title ||
        formatTranslation(intl, InspectionTemplateTranslationKeys.wizardSectionPlaceholder)
      }
      renderItem={(node, itemIndex) => {
        const expanded = expandedIds?.has(node.id);

        return (
          <MemoChecklistItemPreview
            key={node.id}
            checklistItem={{
              templateId,
              _id: node.id,
              summary: node.data.summary,
              norm: {
                description: node.data.norm.description,
              },
              index: itemIndex,
              order: node.data.order,
              parentSectionId: node.data.parentSectionId,
              answers: node.data.answers,
              deleted: node.data.deleted,
            }}
            index={Math.max(itemIndex, 0)}
            displayNumber={node.displayNumber}
            showStatusIcon={false}
            canChangeUrl={true}
            expanded={expanded}
            onExpandedChange={(isExpanded) =>
              onExpandedChange?.(node.id, isExpanded)
            }
          />
        );
      }}
      renderSectionContent={(node) => (
        <>
          {node.data.description ? (
            <RichTextView content={node.data.description} />
          ) : null}
          <SectionDocumentsPreview
            templateId={templateId}
            sectionId={node.id}
          />
        </>
      )}
    />
  );
}

export const MemoChecklistTreePreview = React.memo(ChecklistTreePreview);
